import React, { useState } from "react";
import moment from "moment";
import { useQuery, useMutation } from "@apollo/client";
import { Row, Col, Alert, message, Select } from "antd";
import { CheckCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useStripe, useElements } from "@stripe/react-stripe-js";

import GET_MY_CREDITS from "graphql/queries/GetMyCredits";
import GET_EXTRA_CREDIT_PRICE from "graphql/queries/GetExtraCreditPrice";
import CREATE_ONE_TIME_PAYMENT from "graphql/mutations/CreateOneTimePayment";
import VNPAY_CREATE_PAYMENT from "graphql/mutations/VnpayCreatePayment";
import { trackTapfiliateConversion } from "utils/tapfiliate";

import Button from "common/button";
import { Price, CurrencySign } from "common/program_selector";
import { CurrencySelector } from "pages/registration/SelectProgramFormStyle";

// hooks
import useModal from "hooks/useModal";

// errors
import useErrors from "hooks/useErrors";
import ErrorAlerting from "common/error_alerting";

// types and utils
import getCurrencySign from "utils/currency";
import { Subscription } from "types";
import { SUPPORTED_CURRENCIES } from "utils/constants";
import { trackPurchase } from "utils/gtm";
import ConfirmExtraCreditPaymentModal from "./ConfirmExtraCreditPaymentModal";
import CreditAmountCounter from "../CreditAmountCounter";
import { CreditItem } from "../styles";
// import exp from "constants";

const { Option } = Select;

const centsToDollarPrice = (cents: number) => {
  const centsInDollars = cents * 0.01;
  const convertToString = centsInDollars.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return convertToString;
};

const listOfDollarCurrency = ["usd", "eur", "sgd", "gbp", "aud", "cad"];

type BuyExtraCreditsProps = {
  currency?: string;
  setCurrency: (value: string) => void;
  activeSubscription?: Subscription;
  currentCurrency?: string;
  paymentType?: string;
};

const BuyExtraCredits = ({
  currency,
  setCurrency,
  activeSubscription,
  currentCurrency,
  paymentType,
}: BuyExtraCreditsProps) => {
  const { t } = useTranslation("my_credits");
  const { errors, handleError, closeError } = useErrors();
  const stripe = useStripe();
  const elements = useElements();

  // states
  const [creditAmount, setCreditAmount] = useState<number>(5);
  const [visible, showModal, closeModal] = useModal();

  // mutations
  const [createOneTimePayment] = useMutation(CREATE_ONE_TIME_PAYMENT, {
    refetchQueries: [{ query: GET_MY_CREDITS, variables: { currency } }],
  });
  const [createVnpayPayment] = useMutation(VNPAY_CREATE_PAYMENT);

  // queries
  const { data, loading } = useQuery(GET_EXTRA_CREDIT_PRICE, {
    variables: {
      currency,
    },
  });

  const {
    extraCreditsPrice: [pricePref],
    me: { paymentMethods },
  } = data || {
    extraCreditsPrice: [],
    me: { paymentMethods: [] },
  };

  const isVnPay = paymentType === "vnpay";
  const CURRENCIES = isVnPay
    ? [{ name: "VND", value: "vnd", locale: ["vi"] }]
    : SUPPORTED_CURRENCIES;

  const { price: pricePerCredit, id: stripePriceId } = pricePref || {};

  const defaultPaymentMethodId = paymentMethods?.find(
    (p: any) => p.default
  )?.id;

  const handleUpdateCounter = (counter: number) => {
    if (creditAmount + counter < 0) return;
    setCreditAmount(creditAmount + counter);
  };

  const handleClick = () => showModal();

  const handlePaying = async () => {
    try {
      const {
        data: {
          createOneTimePayment: { invoice },
        },
      } = await createOneTimePayment({
        variables: {
          input: {
            attributes: {
              items: Array.from({ length: creditAmount }, () => stripePriceId),
              amount: creditAmount,
              expiredAt: moment().add(28, "days").format("YYYY-MM-DD HH:mm:ss"),
              creditType: "extra",
              stripePriceId,
              currency,
            },
          },
        },
      });

      const { id, total, customer } = invoice;

      const dataTracking = {
        id: stripePriceId,
        name: "extra",
        price: total * 0.01,
        currency,
        credits: creditAmount,
        type: "extra",
      };

      if (invoice.status === "paid") {
        trackTapfiliateConversion(id, total * 0.01, customer, currency);
        // track purchase for buy extra credit
        trackPurchase(dataTracking);

        closeModal();
        message.success(t("Additional credit bought successfully!"));
      }

      if (invoice.status !== "paid") {
        const { paymentIntent } = invoice;

        if (paymentIntent.status === "requires_action") {
          if (!stripe || !elements) {
            // Stripe.js has not yet loaded.
            // Make sure to disable form submission until Stripe.js has loaded.
            return;
          }

          const result = await stripe.confirmCardPayment(
            paymentIntent.clientSecret
          );
          if (result.error) {
            throw result.error;
          } else if (result?.paymentIntent?.status === "succeeded") {
            trackTapfiliateConversion(id, total * 0.01, customer, currency);
            // track purchase credit for buy extra credit
            trackPurchase(dataTracking);

            closeModal();
            message.success(t("Additional credit bought successfully!"));
          } else {
            throw new Error(
              "Something wrong happened. Your payment cannot be processed."
            );
          }
        }
      }
    } catch (e) {
      handleError(
        new Error(
          t(
            `Your credit card was declined by the bank. Try again. If it still does not work, add another credit card in "MY BILLING", make it your default payment method and try again.`
          )
        )
      );
      closeModal();
    }
  };

  const handleVnpayCreatePayment = async (): Promise<void> => {
    const amount = creditAmount;
    // Store credits info into VnpayOrder, then create at vnpay_controller
    const orderDesc = {
      productName: "Extra Credits",
      amount,
      expiredAt: moment().add(28, "days").format("YYYY-MM-DD HH:mm:ss"),
      creditType: "extra",
      stripePriceId,
    };
    const returnUrl = `${window.location.origin}/return_url`;

    try {
      const {
        data: {
          vnpayCreatePayment: { url },
        },
      } = await createVnpayPayment({
        variables: {
          input: {
            amount: amount * pricePerCredit,
            orderDesc,
            returnUrl,
          },
        },
      });

      window.location.href = `${url}`;
    } catch (e) {
      handleError(e);
    }
  };

  const renderCheckout = () => {
    if (loading) return <Alert type="info" message={t("Loading")} />;
    if (!stripePriceId)
      return (
        <Alert
          type="info"
          message={t(
            "Buying extra credits is not available with this currency. Please select another currency."
          )}
        />
      );
    return (
      <div style={{ flexGrow: 1 }}>
        <CreditItem style={{ flexGrow: 1 }}>
          <div style={{ alignItems: "center" }}>
            <CheckCircleOutlined
              style={{ fontSize: 22, marginLeft: 10, marginRight: 10 }}
            />
            <span>
              {t("{{ creditAmount }} Additional Credit", {
                creditAmount,
                count: creditAmount,
              })}
            </span>
          </div>
          <Price>
            <CurrencySign style={{ marginRight: "5px" }}>
              <span // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: getCurrencySign(currency?.toString() || ""),
                }}
              />
            </CurrencySign>
            {listOfDollarCurrency.includes(currency as string)
              ? centsToDollarPrice(pricePerCredit * creditAmount)
              : (pricePerCredit * creditAmount).toLocaleString(undefined, {
                  maximumFractionDigits: 2,
                  // customIncludes(currency, listOfDollarCurrency)
                })}
          </Price>
        </CreditItem>
        <ErrorAlerting errors={errors} onClose={closeError} />
        <br />
        <div style={{ textAlign: "right" }}>
          <Button
            onClick={() => handleClick()}
            disabled={
              paymentType === "stripe" &&
              (!defaultPaymentMethodId ||
                (activeSubscription?.status !== "active" &&
                  activeSubscription?.status !== "trialing"))
            }
          >
            {t("CHECKOUT")}
          </Button>
        </div>
      </div>
    );
  };

  const renderCurrencySelector = () => (
    <CurrencySelector
      value={currency}
      onChange={(value) => setCurrency(value as string)}
      style={{ width: 90, top: -10 }}
    >
      {CURRENCIES.map(({ value, name }) => (
        <Option value={value} key={value}>
          {name}
        </Option>
      ))}
    </CurrencySelector>
  );

  return (
    <Row gutter={[15, 15]}>
      <Col xs={24} sm={24} md={6}>
        <CreditAmountCounter
          creditAmount={creditAmount}
          onClickCounter={handleUpdateCounter}
        />
      </Col>
      <Col xs={24} sm={24} md={12}>
        {renderCheckout()}
      </Col>
      {/* Only allow student to choose currency once.
      If there's an active subscription already, do not allow them to choose other. */}
      {!currentCurrency && (
        <Col
          md={6}
          sm={24}
          style={{ display: "flex", justifyContent: "flex-end" }}
        >
          {renderCurrencySelector()}
        </Col>
      )}

      <ConfirmExtraCreditPaymentModal
        visible={visible}
        creditAmount={creditAmount}
        onCancel={() => closeModal()}
        onConfirm={isVnPay ? handleVnpayCreatePayment : handlePaying}
      />
    </Row>
  );
};

export default BuyExtraCredits;
